import React, { useEffect, useRef, useState } from 'react';

import { css } from '@emotion/css';
import { IntegratedErps } from '@recurrency/core-api-schema/dist/common/enums';
import { notification, Progress } from 'antd';

import { NotificationLink } from 'components/Links';

import { captureAndShowError, HttpError } from 'utils/error';

import { getErpName } from './formatting';
import { routes } from './routes';

let notificationCounter = 0; // to ensure uniqueness
const DEFAULT_EXPECTED_WAIT_SECONDS = 300;

export function createSubmissionNotification({
  entityName,
  erpType,
  expectedWaitSeconds,
  submittingMessage,
  fireInProgressNotification = true,
}: {
  entityName: string;
  erpType?: IntegratedErps;
  /** Expected time to complete submission in seconds */
  expectedWaitSeconds: number;
  submittingMessage?: string;
  fireInProgressNotification?: boolean;
}) {
  const notificationStartMs = Date.now();
  const notificationKey = `submission-${notificationCounter++}`;
  submittingMessage = submittingMessage || `Submitting ${entityName} ${erpType ? `to ${getErpName(erpType)}` : ''}`;

  const inProgress = (
    options: {
      description?: (notificationKey: string, entityName: string) => React.ReactNode;
    } = {},
  ) => {
    notification.info({
      message: submittingMessage,
      key: notificationKey,
      description: (
        <div
          className={css`
            margin-top: 8px;
          `}
        >
          {expectedWaitSeconds > 1 ? (
            <ExpectedWaitProgress expectedWaitSeconds={expectedWaitSeconds ?? DEFAULT_EXPECTED_WAIT_SECONDS} showInfo />
          ) : null}
          {options.description ? (
            <>
              <br />
              {options.description(notificationKey, entityName)}
            </>
          ) : null}
        </div>
      ),
      duration: 0,
    });
  };

  if (fireInProgressNotification) inProgress();

  const success = (
    options: {
      successMessage?: string;
      description?: (notificationKey: string, entityName: string) => React.ReactNode;
      duration?: number;
    } = {},
  ) => {
    const notifySuccess = () => {
      notification.success({
        key: notificationKey,
        message: options.successMessage || `${entityName} submitted`,
        description: options.description?.(notificationKey, entityName),
        duration: options.duration || 10,
      });
    };

    // Show almost done message if it took longer than 5 seconds, otherwise keep it fast and simple
    if (Date.now() - notificationStartMs > 5_000) {
      notification.info({
        message: submittingMessage,
        key: notificationKey,
        description: (
          <div
            className={css`
              margin-top: 8px;
            `}
          >
            Almost done...
            <ExpectedWaitProgress initialProgress={100} expectedWaitSeconds={0} />
          </div>
        ),
        duration: 0,
      });
      setTimeout(notifySuccess, 1000);
    } else {
      notifySuccess();
    }
  };

  const error = (
    err: unknown,
    options: {
      description?: (notificationKey: string, entityName: string) => React.ReactNode;
    } = {},
  ) => {
    if (err instanceof HttpError && err.statusCode === /* Request Timeout */ 408) {
      // show info message not error - so the user doesn't get frustrated
      notification.info({
        message: submittingMessage,
        key: notificationKey,
        description: (
          <div
            className={css`
              margin-top: 8px;
            `}
          >
            Connection to live status updates was interrupted. For the latest export updates, please visit the status
            page.
            <div>
              <NotificationLink notificationKey={notificationKey} to={routes.monitoring.exportJobs()}>
                View Export Jobs
              </NotificationLink>{' '}
              to track your submission.
            </div>
          </div>
        ),
        duration: 0,
      });
      return;
    }

    captureAndShowError(err, `Unable to submit ${entityName}`, {
      notificationKey,
      renderDescription: (errorDescription) => (
        <>
          {/** filter out anything errors with MotherDuck (case sensitive) in it * */}
          {errorDescription && errorDescription.includes('MotherDuck')
            ? 'Internal sync issue. Recurrency team is alerted.'
            : errorDescription}
          {options.description ? (
            <>
              <br />
              {options.description(notificationKey, entityName)}
            </>
          ) : null}
        </>
      ),
    });
  };

  return { success, error, inProgress, notificationKey, notificationStartMs } as const;
}

export interface ExpectedWaitProgressProps {
  expectedWaitSeconds: number;
  initialProgress?: number;
  showInfo?: boolean;
}

export function ExpectedWaitProgress({
  expectedWaitSeconds,
  showInfo,
  initialProgress = 0,
}: ExpectedWaitProgressProps) {
  const percentagePoint = 100 / (expectedWaitSeconds * 10);
  const [currentProgress, setCurrentProgress] = useState(initialProgress);
  const stepRef = useRef(1 * percentagePoint);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentProgress > 95) {
        stepRef.current = 0;
      } else if (currentProgress > 90) {
        stepRef.current = 0.2 * percentagePoint;
      } else if (currentProgress > 80) {
        stepRef.current = 0.5 * percentagePoint;
      } else if (currentProgress > 50) {
        stepRef.current = 0.75 * percentagePoint;
      }
      setCurrentProgress(currentProgress + stepRef.current);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [currentProgress, percentagePoint]);

  return (
    <>
      {showInfo &&
        `Estimate: ${estimatedSecondsToHumanReadable(
          expectedWaitSeconds / (100 / (100 - currentProgress)),
        )} remaining...`}
      <Progress percent={currentProgress} showInfo={false} />
    </>
  );
}

export function estimatedSecondsToHumanReadable(seconds: number): string {
  if (seconds <= 10) {
    return `10 seconds`;
  }
  if (seconds <= 15) {
    return `15 seconds`;
  }
  if (seconds <= 30) {
    return `30 seconds`;
  }
  if (seconds <= 60) {
    return `1 minute`;
  }
  return `${Math.ceil(seconds / 60)} minutes`;
}
